import { useState, FormEvent } from 'react';

export default function Contact() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d518ogo9ei8qjpb9fqlg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#1A3A3A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1200 600">
          <path d="M0 300 Q 300 200 600 300 T 1200 300" stroke="white" strokeWidth="1" fill="none" opacity="0.3"/>
          <path d="M0 250 Q 300 150 600 250 T 1200 250" stroke="white" strokeWidth="1" fill="none" opacity="0.2"/>
          <circle cx="200" cy="200" r="3" fill="white" opacity="0.4"/>
          <circle cx="500" cy="300" r="3" fill="white" opacity="0.4"/>
          <circle cx="800" cy="250" r="3" fill="white" opacity="0.4"/>
          <circle cx="1000" cy="350" r="3" fill="white" opacity="0.4"/>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className="grid grid-cols-2 gap-16 mb-16">
          <div>
            <div className="flex items-center gap-2 mb-8">
              <span className="text-2xl font-serif text-white">POLONYI S.R.L.</span>
              <span className="w-2 h-2 bg-[#B8E986] rounded-full"></span>
            </div>

            <form onSubmit={handleSubscribe} className="mb-6" data-readdy-form>
              <div className="flex items-center gap-3">
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-5 py-3 bg-transparent border border-white/30 rounded-lg text-white placeholder-white/50 text-sm focus:outline-none focus:border-[#B8E986] transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-12 h-12 bg-[#B8E986] rounded-full flex items-center justify-center hover:bg-[#A5D675] transition-colors disabled:opacity-50"
                >
                  <i className="ri-notification-3-line text-gray-900 text-xl"></i>
                </button>
              </div>
            </form>

            {submitStatus === 'success' && (
              <p className="text-[#B8E986] text-sm mb-4">Thank you for subscribing!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-sm mb-4">Something went wrong. Please try again.</p>
            )}

            <h3 className="text-3xl font-serif text-white leading-relaxed">
              Stay Updated on<br />Security & Privacy
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-12">
            <div>
              <h4 className="text-white font-medium mb-4 text-sm">Product</h4>
              <ul className="space-y-3">
                <li><a href="#features" className="text-white/70 text-sm hover:text-white transition-colors">VPN Features</a></li>
                <li><a href="#about" className="text-white/70 text-sm hover:text-white transition-colors">Security</a></li>
                <li><a href="#about" className="text-white/70 text-sm hover:text-white transition-colors">Privacy</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4 text-sm">Company</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-white/70 text-sm hover:text-white transition-colors">About Us</a></li>
                <li><a href="#contact" className="text-white/70 text-sm hover:text-white transition-colors">Contact</a></li>
                <li><a href="#support" className="text-white/70 text-sm hover:text-white transition-colors">Support</a></li>
              </ul>
            </div>

            <div className="col-span-2">
              <h4 className="text-white font-medium mb-4 text-sm">Contact Information</h4>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-white/70 text-sm">
                  <i className="ri-mail-line"></i>
                  <span>information@polonyisrl.com</span>
                </li>
                <li className="flex items-center gap-2 text-white/70 text-sm">
                  <i className="ri-phone-line"></i>
                  <span>+1 3143386794</span>
                </li>
                <li className="flex items-start gap-2 text-white/70 text-sm">
                  <i className="ri-map-pin-line mt-0.5"></i>
                  <span>Av. San Martin 3687, Eldorado, N3380ACK, Argentina</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm">© 2025 POLONYI S.R.L. All rights reserved</p>
            
            <div className="flex items-center gap-4">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#B8E986] transition-colors">
                <i className="ri-linkedin-line text-white text-lg"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#B8E986] transition-colors">
                <i className="ri-twitter-x-line text-white text-lg"></i>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#B8E986] transition-colors">
                <i className="ri-facebook-line text-white text-lg"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#B8E986] transition-colors">
                <i className="ri-instagram-line text-white text-lg"></i>
              </a>
            </div>

            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 text-sm hover:text-white transition-colors">
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
